import { evalCondition } from './cds.js';
import { dailyDose, drugOf } from './context.js';
import { evaluate, parse } from './expr.js';
import { EXT_CDS_PROVENANCE, EXT_EPISODE_END_REASON, EXT_MOTHER, EXT_NEXT_VISIT, SYS_ACT_CODE, SYS_CONCEPT, SYS_DRUG, SYS_FORM, SYS_ICD11, SYS_ICD11_ENTITY, SYS_LOINC, SYS_PROGRAMME, } from './fhir.js';
import { newId } from './ids.js';
import { labOrderResource, referralResource } from './referral.js';
export const dailyOf = (r) => (r ? r.dose * r.timesPerDay : 0);
/** Calendar arithmetic on clinic dates (YYYY-MM-DD). */
export function addDays(isoDate, days) {
    const [y, m, d] = isoDate.slice(0, 10).split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}
export function splitRecord(resources) {
    const out = {
        episodes: [],
        conditions: [],
        encounters: [],
        observations: [],
        medications: [],
        tasks: [],
        referrals: [],
    };
    for (const r of resources) {
        if (r.resourceType === 'EpisodeOfCare')
            out.episodes.push(r);
        else if (r.resourceType === 'Condition')
            out.conditions.push(r);
        else if (r.resourceType === 'Encounter')
            out.encounters.push(r);
        else if (r.resourceType === 'Observation')
            out.observations.push(r);
        else if (r.resourceType === 'MedicationRequest')
            out.medications.push(r);
        else if (r.resourceType === 'Task')
            out.tasks.push(r);
        else if (r.resourceType === 'ServiceRequest')
            out.referrals.push(r);
    }
    out.encounters.sort((a, b) => b.period.start.localeCompare(a.period.start));
    return out;
}
export function activeProgrammes(episodes) {
    return episodes
        .filter((e) => e.status === 'active')
        .flatMap((e) => e.type.flatMap((t) => t.coding ?? []))
        .filter((c) => c.system === SYS_PROGRAMME && c.code)
        .map((c) => c.code);
}
/** The medicines the patient is on now, from active prescriptions. */
export function currentRegimen(meds) {
    const out = {};
    for (const m of meds) {
        if (m.status !== 'active')
            continue;
        const code = drugOf(m);
        if (!code)
            continue;
        const times = m.dosageInstruction?.[0]?.timing?.repeat?.frequency ?? 1;
        const prev = out[code];
        const daily = dailyOf(prev) + dailyDose(m);
        const timesPerDay = prev ? Math.max(prev.timesPerDay, times) : times;
        out[code] = { dose: daily / timesPerDay, timesPerDay };
    }
    return out;
}
/** A sensible default for how often a daily total is given; the provider can change it. */
export function defaultTimes(drug, daily) {
    return drug.split_above !== undefined && daily > drug.split_above ? 2 : 1;
}
export function regimenFor(drug, daily) {
    if (!(daily > 0))
        return undefined;
    const timesPerDay = defaultTimes(drug, daily);
    return { dose: daily / timesPerDay, timesPerDay };
}
export function sameRegimenItem(a, b) {
    if (dailyOf(a) === 0 && dailyOf(b) === 0)
        return true;
    return !!a && !!b && a.dose === b.dose && a.timesPerDay === b.timesPerDay;
}
// ------------------------------------------------------------------ suggestions
/**
 * The regimen after applying a rule's suggestion, or null when the suggestion cannot be applied
 * automatically (no medicine named, or no dose to work out). Nothing is saved: the provider sees
 * the change in the medicine list and decides.
 */
export function applySuggestion(pack, regimen, out) {
    if (out.action !== 'suggest')
        return null;
    const drug = pack.drugs.find((d) => d.code === out.params.drug);
    if (!drug)
        return null;
    const current = dailyOf(regimen[drug.code]);
    const steps = drug.doses.filter((d) => d > 0).sort((a, b) => a - b);
    const maxDose = steps[steps.length - 1] ?? 0;
    let target = null;
    const change = out.params.change ?? 'start';
    if (out.params.dose)
        target = Number(out.params.dose);
    else if (change === 'increase' && out.params.step)
        target = Math.min(current + Number(out.params.step), maxDose);
    else if (change === 'reduce')
        target = [...steps].reverse().find((d) => d < current) ?? null;
    else if (change === 'stop')
        target = 0;
    if (target === null || !Number.isFinite(target))
        return null;
    if (change === 'increase' && target <= current)
        return null;
    const next = { ...regimen };
    for (const s of (out.params.stop ?? '').split(',').filter(Boolean))
        delete next[s.trim()];
    const item = regimenFor(drug, target);
    if (item)
        next[drug.code] = item;
    else
        delete next[drug.code];
    return next;
}
// ------------------------------------------------------------------ the form
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
/** Fill calculated concepts (e.g. BMI) from the other values. */
export function withCalculated(concepts, form) {
    const out = { ...form };
    for (const c of concepts) {
        if (!c.calc)
            continue;
        const v = evaluate(parse(c.calc), out);
        if (!isNum(v)) {
            delete out[c.code];
            continue;
        }
        const f = 10 ** (c.decimals ?? 0);
        out[c.code] = Math.round(v * f) / f;
    }
    return out;
}
/** Concepts on the form that are visible for this context (section and field show_if). */
export function visibleConcepts(def, ctx) {
    const out = new Set();
    for (const s of def.sections) {
        if (!evalCondition(s.show_if, ctx))
            continue;
        for (const f of s.fields)
            if (evalCondition(f.show_if, ctx))
                out.add(f.concept);
    }
    return out;
}
/** Problem with a typed value, or null. Range limits come from the concept. */
/**
 * The diagnoses picked in a `condition` concept, each as a FHIR Condition of this encounter, coded
 * with its ICD-11 code, title and WHO URI when the choice has them (the title always as text).
 */
export function diagnosisConditions(c, value, patientId, encounterId, nowIso, makeId = newId) {
    const picked = String(value).split(',').filter(Boolean);
    return picked.flatMap((code) => {
        const ch = c.choices?.find((x) => String(x.code) === code);
        if (!ch)
            return [];
        const coding = [
            ...(ch.icd11 ? [{ system: SYS_ICD11, code: ch.icd11, display: ch.display }] : []),
            ...(ch.uri ? [{ system: SYS_ICD11_ENTITY, code: ch.uri, display: ch.display }] : []),
        ];
        return [
            {
                resourceType: 'Condition',
                id: makeId(),
                clinicalStatus: {
                    coding: [
                        { system: 'http://terminology.hl7.org/CodeSystem/condition-clinical', code: 'active' },
                    ],
                },
                verificationStatus: {
                    coding: [
                        {
                            system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status',
                            code: 'confirmed',
                        },
                    ],
                },
                category: [
                    {
                        coding: [
                            {
                                system: 'http://terminology.hl7.org/CodeSystem/condition-category',
                                code: 'encounter-diagnosis',
                            },
                        ],
                    },
                ],
                code: { ...(coding.length ? { coding } : {}), text: ch.display },
                subject: ref('Patient', patientId),
                encounter: ref('Encounter', encounterId),
                recordedDate: nowIso,
            },
        ];
    });
}
export function checkValue(c, v) {
    if (v === null || v === undefined || v === '')
        return null;
    if (c.type === 'quantity' || c.type === 'integer') {
        if (!isNum(v))
            return 'Enter a number';
        if (c.type === 'integer' && !Number.isInteger(v))
            return 'Enter a whole number';
        if (c.min !== undefined && v < c.min)
            return `Must be at least ${c.min}`;
        if (c.max !== undefined && v > c.max)
            return `Must be at most ${c.max}`;
    }
    if (c.type === 'datetime' && (typeof v !== 'string' || !FHIR_DATETIME_RE.test(v)))
        return 'Enter a date and time';
    if (c.type === 'multi' && c.choices?.length) {
        const ok = String(v)
            .split(',')
            .filter(Boolean)
            .every((p) => c.choices?.some((x) => String(x.code) === p));
        return ok ? null : 'Choose from the options';
    }
    if (c.choices?.length && !c.choices.some((x) => String(x.code) === String(v)))
        return 'Choose one of the options';
    if (c.type === 'text' && String(v).length > 2000)
        return 'At most 2,000 characters';
    return null;
}
// ------------------------------------------------------------------ resources to save
const FHIR_DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;
const ref = (type, id) => ({ reference: `${type}/${id}` });
export function enrolmentResources(programme, patientId, today, nowIso, makeId = newId) {
    const episode = {
        resourceType: 'EpisodeOfCare',
        id: makeId(),
        status: 'active',
        type: [
            { coding: [{ system: SYS_PROGRAMME, code: programme.code, display: programme.display }] },
        ],
        patient: ref('Patient', patientId),
        period: { start: today },
    };
    const condition = {
        resourceType: 'Condition',
        id: makeId(),
        clinicalStatus: {
            coding: [
                {
                    system: 'http://terminology.hl7.org/CodeSystem/condition-clinical',
                    code: 'active',
                },
            ],
        },
        verificationStatus: {
            coding: [
                {
                    system: 'http://terminology.hl7.org/CodeSystem/condition-ver-status',
                    code: 'confirmed',
                },
            ],
        },
        code: {
            ...(programme.icd11
                ? { coding: [{ system: SYS_ICD11, code: programme.icd11, display: programme.display }] }
                : {}),
            text: programme.display,
        },
        subject: ref('Patient', patientId),
        recordedDate: nowIso,
    };
    return [episode, condition];
}
function observationFor(c, v, base, makeId) {
    const coding = [
        { system: SYS_CONCEPT, code: c.code, display: c.display },
        ...(c.loinc ? [{ system: SYS_LOINC, code: c.loinc }] : []),
    ];
    const o = {
        resourceType: 'Observation',
        id: makeId(),
        status: 'final',
        code: { coding, text: c.display },
        ...base,
    };
    if (c.type === 'boolean')
        o.valueBoolean = v === true;
    else if (c.type === 'choice') {
        const ch = c.choices?.find((x) => String(x.code) === String(v));
        o.valueCodeableConcept = {
            coding: [{ system: `${SYS_CONCEPT}/${c.code}`, code: String(v), display: ch?.display }],
        };
    }
    else if ((c.type === 'quantity' || c.type === 'integer') && typeof v === 'number') {
        if (c.unit)
            o.valueQuantity = { value: v, unit: c.unit };
        else if (c.type === 'integer')
            o.valueInteger = v;
        else
            o.valueQuantity = { value: v };
    }
    else if (c.type === 'datetime')
        o.valueDateTime = String(v);
    else
        o.valueString = String(v);
    return o;
}
const timesText = (n) => n === 1 ? 'once daily' : n === 2 ? 'twice daily' : `${n} times daily`;
function prescription(drug, item, patientId, encounterId, nowIso, makeId) {
    const dose = Math.round(item.dose * 100) / 100;
    return {
        resourceType: 'MedicationRequest',
        id: makeId(),
        status: 'active',
        intent: 'order',
        medicationCodeableConcept: {
            coding: [{ system: SYS_DRUG, code: drug.code, display: drug.display }],
            text: drug.display,
        },
        subject: ref('Patient', patientId),
        encounter: ref('Encounter', encounterId),
        authoredOn: nowIso,
        dosageInstruction: [
            {
                text: `${drug.display} ${dose} ${drug.unit} ${timesText(item.timesPerDay)}`,
                timing: { repeat: { frequency: item.timesPerDay, period: 1, periodUnit: 'd' } },
                doseAndRate: [{ doseQuantity: { value: dose, unit: drug.unit } }],
            },
        ],
    };
}
const extsOfAny = (r) => (r.extension ?? []);
/** Close an open episode (a labour that ended, a woman referred out), keeping its record. */
export function closeEpisode(e, reason, today) {
    return {
        ...e,
        status: reason === 'entered-in-error' ? 'entered-in-error' : 'finished',
        period: { ...(e.period ?? { start: today }), end: today },
        extension: [
            ...extsOfAny(e).filter((x) => x.url !== EXT_EPISODE_END_REASON),
            { url: EXT_EPISODE_END_REASON, valueCode: reason },
        ],
    };
}
/** A newborn's or exposed infant's mother (Patient id), when the record is linked. */
export function motherOf(p) {
    const ext = (p?.extension ?? []).find((x) => x.url === EXT_MOTHER);
    const ref = ext?.valueReference?.reference ?? '';
    return ref.startsWith('Patient/') ? ref.slice(8) : null;
}
/** The pack form an Encounter recorded, if any. */
export function formOfEncounter(e) {
    for (const t of e.type ?? [])
        for (const c of t.coding ?? [])
            if (c.system === SYS_FORM)
                return c.code ?? null;
    return null;
}
/**
 * What saving the visit does by itself, from the advice: `enrol` rules marked auto=true open an
 * episode, `close` rules end one.
 */
export function saveEffects(result) {
    const outs = result?.outputs ?? [];
    return {
        deceased: outs.some((o) => o.action === 'deceased'),
        enrol: outs
            .filter((o) => o.action === 'enrol' && o.params.auto === 'true' && o.params.programme)
            .map((o) => o.params.programme),
        close: outs
            .filter((o) => o.action === 'close' && o.params.programme)
            .map((o) => ({
            programme: o.params.programme,
            reason: (o.params.reason ?? 'completed'),
        })),
    };
}
/**
 * The resources a visit produces, in save order: enrolments, the Encounter, Observations, then
 * medicine changes (old prescriptions stopped, new ones written). A medicine whose regimen is
 * unchanged keeps its prescription, so "days since the last change" stays true.
 */
export function visitResources(v) {
    const makeId = v.makeId ?? newId;
    const out = [];
    const pid = v.patient.id;
    const episodes = [...v.record.episodes];
    for (const code of v.enrol) {
        if (activeProgrammes(episodes).includes(code))
            continue;
        const prog = v.pack.programmes.find((p) => p.code === code);
        if (!prog)
            continue;
        const [ep, cond] = enrolmentResources(prog, pid, v.today, v.nowIso, makeId);
        episodes.push(ep);
        // An episode (a labour) is not a diagnosis to carry for life: no Condition for it.
        // A diagnosis only for a chronic condition (with its ICD-11 code), not for a pregnancy or a labour.
        out.push(ep, ...(prog.episode || !prog.icd11 ? [] : [cond]));
    }
    const progs = activeProgrammes(episodes).filter((c) => v.pack.programmes.some((p) => p.code === c));
    const encId = makeId();
    // Rules shown at the visit (predictions are not recorded here).
    const shown = (v.result?.outputs ?? []).filter((o) => o.action !== 'classify' && o.action !== 'predict');
    const enc = {
        resourceType: 'Encounter',
        id: encId,
        status: 'finished',
        class: { system: SYS_ACT_CODE, code: 'AMB', display: 'ambulatory' },
        ...(progs.length
            ? {
                type: progs.map((c) => ({
                    coding: [
                        {
                            system: SYS_PROGRAMME,
                            code: c,
                            display: v.pack.programmes.find((p) => p.code === c)?.display,
                        },
                    ],
                })),
            }
            : {}),
        subject: ref('Patient', pid),
        period: { start: v.nowIso, end: v.nowIso },
    };
    const formId = v.formId ?? v.pack.forms[0]?.id;
    if (formId)
        enc.type = [
            ...(enc.type ?? []),
            {
                coding: [
                    {
                        system: SYS_FORM,
                        code: formId,
                        display: v.pack.forms.find((f) => f.id === formId)?.title,
                    },
                ],
            },
        ];
    const eps = episodes.filter((e) => e.status === 'active' &&
        e.type.some((t) => t.coding?.some((c) => c.system === SYS_PROGRAMME && progs.includes(c.code ?? ''))));
    if (eps.length)
        enc.episodeOfCare = eps.map((e) => ref('EpisodeOfCare', e.id));
    const ext = [];
    if (v.nextVisit)
        ext.push({ url: EXT_NEXT_VISIT, valueDate: v.nextVisit });
    if (v.result)
        ext.push({
            url: EXT_CDS_PROVENANCE,
            extension: [
                { url: 'pack', valueString: `${v.result.packId}@${v.result.packVersion}` },
                { url: 'contentHash', valueString: v.pack.contentHash },
                ...shown.map((o) => ({
                    url: 'shown',
                    valueString: `${o.ruleId}@${o.ruleVersion}${o.draft ? ' (draft)' : ''}`,
                })),
            ],
        });
    if (ext.length)
        enc.extension = ext;
    out.push(enc);
    const obsBase = {
        subject: ref('Patient', pid),
        encounter: ref('Encounter', encId),
        effectiveDateTime: v.nowIso,
    };
    const formDef = v.pack.forms.find((f) => f.id === formId);
    const onForm = new Set(formDef?.sections.flatMap((s) => s.fields.map((f) => f.concept)) ?? []);
    for (const c of v.pack.concepts) {
        // a stored classification is recorded only with the form it belongs to
        if (c.store_from && !onForm.has(c.code))
            continue;
        // (a stored classification comes from the rules' result; a migrated visit supplies it on the form)
        const val = c.store_from
            ? (v.result?.derived[c.store_from] ?? v.form[c.code] ?? null)
            : v.form[c.code];
        if (val === null || val === undefined || val === '')
            continue;
        out.push(observationFor(c, val, obsBase, makeId));
        if (c.condition)
            out.push(...diagnosisConditions(c, val, pid, encId, v.nowIso, makeId));
    }
    const before = currentRegimen(v.record.medications);
    for (const drug of v.pack.drugs) {
        const now = v.regimen[drug.code];
        if (sameRegimenItem(before[drug.code], now))
            continue;
        for (const m of v.record.medications)
            if (m.status === 'active' && drugOf(m) === drug.code)
                out.push({ ...m, status: 'stopped' });
        if (dailyOf(now) > 0 && now)
            out.push(prescription(drug, now, pid, encId, v.nowIso, makeId));
    }
    for (const r of v.referrals ?? [])
        out.push(referralResource({ ...r, patientId: pid, encounterId: encId, nowIso: v.nowIso, makeId }));
    if (v.deceased && !v.patient.deceasedBoolean && !v.patient.deceasedDateTime)
        out.push({ ...v.patient, deceasedDateTime: v.nowIso });
    for (const l of v.labOrders ?? [])
        out.push(labOrderResource({ ...l, patientId: pid, encounterId: encId, nowIso: v.nowIso, makeId }));
    for (const c of v.close ?? []) {
        for (const e of episodes) {
            if (e.status !== 'active')
                continue;
            if (!e.type.some((t) => t.coding?.some((x) => x.system === SYS_PROGRAMME && x.code === c.programme)))
                continue;
            out.push(closeEpisode(e, c.reason, v.today));
        }
    }
    return out;
}
/** The next-visit date recorded on an encounter, if any. */
export const nextVisitOf = (e) => (e.extension ?? []).find((x) => x.url === EXT_NEXT_VISIT)?.valueDate ?? null;
//# sourceMappingURL=visit.js.map